import { z } from 'zod';

import type { TestFitAuthorityRef } from './generationImpact';
import {
  documentCitationV1Schema,
  documentClaimV1Schema,
  documentOmissionV1Schema,
  evidenceValueJsonSchema,
  jsonValueSchema,
  reportDocumentBlockV1Schema,
  testFitAuthorityRefSchema,
} from './reportContractsV2';

export const DECISION_ANALYSIS_SCHEMA_VERSION = 'decision-analysis-v1' as const;
export const DECISION_ANALYSIS_INPUT_SCHEMA_VERSION = 'decision-analysis-input-v1' as const;
export const DECISION_ANALYSIS_HASH_VERSION = 'canonical-json-sha256-v1' as const;
/**
 * v2 — content-addressed SED identity. The input-hash payload carries the
 * SED's normalized CONTENT hash only (see lib/decisionAnalysis/
 * sedContentIdentity.ts); `sed.id` / `sed.version` stay on the envelope as
 * provenance but no longer participate in the identity, so a rebuild that
 * reproduces identical evidence (new sedId, bumped sedVersion) keeps the
 * analysis decision-current. v1 snapshots remain valid and are compared
 * under their own scheme (see decisionAnalysisInputsCurrent).
 */
export const DECISION_ANALYSIS_HASH_VERSION_V2 = 'canonical-json-sha256-v2' as const;
export const DECISION_ANALYSIS_DOCUMENT_SCHEMA_VERSION =
  'decision-analysis-document-v1' as const;
export const ANALYSIS_READINESS_DECISION_SCHEMA_VERSION =
  'analysis-readiness-decision-v1' as const;
export const ANALYSIS_REVISION_REFERENCE_SCHEMA_VERSION =
  'analysis-revision-ref-v1' as const;

/**
 * Decision domains deliberately exclude presentation-only report types.
 * A custom brief or executive summary may project these analyses, but neither
 * is itself an authoritative decision input.
 */
export const decisionAnalysisDomainSchema = z.enum([
  'feasibility',
  'environmental',
  'test-fit',
  'power-availability',
  'water-resource',
  'interconnection',
  'public-sentiment',
  'supply-chain-risk',
  'financial',
]);

export type DecisionAnalysisDomain = z.infer<typeof decisionAnalysisDomainSchema>;

const nonEmptyString = z.string().trim().min(1);
const sha256 = z.string().regex(/^[a-f0-9]{64}$/);
const isoDateString = z.string().datetime({ offset: true });

/** v1 (identity-coupled SED hash) and v2 (content-addressed SED hash). */
export const decisionAnalysisHashVersionSchema = z.union([
  z.literal(DECISION_ANALYSIS_HASH_VERSION),
  z.literal(DECISION_ANALYSIS_HASH_VERSION_V2),
]);

export type DecisionAnalysisHashVersion =
  z.infer<typeof decisionAnalysisHashVersionSchema>;

/** Exact semantic SED input exposed to this report, independently of the
 * collection revision. Full-document scope is reserved for unconstrained
 * synthesis products such as a Custom Brief. */
export const reportSedMaterialDependencyManifestV1Schema = z.object({
  schemaVersion: z.literal('report-sed-material-dependencies-v1'),
  reportType: nonEmptyString,
  scope: z.enum(['report-prompt', 'full-document']),
  contentHash: sha256,
  fieldHashes: z.record(nonEmptyString, sha256),
}).strict();

export type ReportSedMaterialDependencyManifestV1 =
  z.infer<typeof reportSedMaterialDependencyManifestV1Schema>;

export const decisionAnalysisSedReferenceV1Schema = z.object({
  id: nonEmptyString,
  version: z.number().int().positive(),
  contentHash: sha256,
  /**
   * Per-section content hashes for the sections this analysis BOUND (hash
   * scheme v2 normalization, section scope). Staleness metadata, NOT identity:
   * the v2 input-hash payload projects `sed` to `{ contentHash }`, so this
   * field never participates in inputHash and old persisted envelopes verify
   * unchanged. Present ⇒ commit-time currency compares only these sections
   * against the site's `sedSectionContentHashes` mirror; absent ⇒ the
   * whole-document comparison applies (conservative).
   */
  sectionHashes: z.record(nonEmptyString, sha256).optional(),
  /**
   * The same bound sections under section identity scheme v3 (v2 plus the
   * cache/provenance-label exclusions, sedContentIdentity.ts). Staleness
   * metadata beside `sectionHashes`, NOT identity: the v2 input-hash payload
   * still projects `sed` to `{ contentHash }`, so inputHash and analysis ids
   * are unchanged. Absent on envelopes built before the scheme existed;
   * currency then keeps its v2 verdicts (`boundSectionUnchanged`).
   */
  sectionHashesV3: z.record(nonEmptyString, sha256).optional(),
  /** New immutable inputs bind this manifest in inputHash. Its absence retains
   * the legacy whole/section-hash policy; missing evidence never proves reuse. */
  materialDependencies: reportSedMaterialDependencyManifestV1Schema.optional(),
}).strict();

export type DecisionAnalysisSedReferenceV1 =
  z.infer<typeof decisionAnalysisSedReferenceV1Schema>;

export const decisionAnalysisIntentReferenceV1Schema = z.discriminatedUnion('state', [
  z.object({
    state: z.literal('absent'),
    contentHash: sha256,
    revision: z.string().nullable(),
    snapshot: z.null(),
  }).strict(),
  z.object({
    state: z.literal('present'),
    contentHash: sha256,
    revision: z.string().nullable(),
    snapshot: jsonValueSchema.refine(
      value => value !== null && !Array.isArray(value) && typeof value === 'object',
      'A present project intent snapshot must be a JSON object.',
    ),
  }).strict(),
]);

export type DecisionAnalysisIntentReferenceV1 =
  z.infer<typeof decisionAnalysisIntentReferenceV1Schema>;

export const decisionAnalysisTypedPreferencesReferenceV1Schema =
  z.discriminatedUnion('state', [
    z.object({
      state: z.literal('absent'),
      contentHash: sha256,
      snapshot: z.null(),
    }).strict(),
    z.object({
      state: z.literal('present'),
      contentHash: sha256,
      snapshot: jsonValueSchema.refine(
        value => value !== null && !Array.isArray(value) && typeof value === 'object',
        'A present typed-preferences snapshot must be a JSON object.',
      ),
    }).strict(),
  ]);

export type DecisionAnalysisTypedPreferencesReferenceV1 =
  z.infer<typeof decisionAnalysisTypedPreferencesReferenceV1Schema>;

export const decisionAnalysisAuthorityV1Schema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('none'),
  }).strict(),
  z.object({
    kind: z.literal('test-fit'),
    ref: testFitAuthorityRefSchema,
  }).strict(),
]);

export type DecisionAnalysisAuthorityV1 =
  | { kind: 'none' }
  | { kind: 'test-fit'; ref: TestFitAuthorityRef };

export const decisionAnalysisUpstreamReferenceV1Schema = z.object({
  kind: nonEmptyString,
  id: nonEmptyString,
  version: nonEmptyString.nullable(),
  contentHash: sha256,
  /** Raw immutable identity remains bound; this additional exact semantic
   * projection can prove only collector/UI metadata changed between refs. */
  materialIdentity: z.object({
    schemaVersion: z.literal('provider-authority-material-v1'),
    contentHash: sha256,
  }).strict().optional(),
}).strict().superRefine((reference, ctx) => {
  if (reference.materialIdentity && reference.kind !== 'provider-authority') {
    ctx.addIssue({ code: 'custom', path: ['materialIdentity'],
      message: 'Material provider identity requires a provider-authority source.' });
  }
});

export type DecisionAnalysisUpstreamReferenceV1 =
  z.infer<typeof decisionAnalysisUpstreamReferenceV1Schema>;

export const decisionAnalysisVersionEnvelopeV1Schema = z.object({
  analysisPolicy: nonEmptyString,
  factModel: nonEmptyString,
  scorer: nonEmptyString,
}).strict();

export type DecisionAnalysisVersionEnvelopeV1 =
  z.infer<typeof decisionAnalysisVersionEnvelopeV1Schema>;

export const decisionAnalysisInputEnvelopeV1Schema = z.object({
  schemaVersion: z.literal(DECISION_ANALYSIS_INPUT_SCHEMA_VERSION),
  hashVersion: decisionAnalysisHashVersionSchema,
  siteId: nonEmptyString,
  domain: decisionAnalysisDomainSchema,
  sed: decisionAnalysisSedReferenceV1Schema,
  intent: decisionAnalysisIntentReferenceV1Schema,
  typedPreferences: decisionAnalysisTypedPreferencesReferenceV1Schema,
  decisionContext: z.object({
    powerStrategy: z.enum(['grid-only', 'btm-only', 'hybrid', 'islanded']).nullable(),
  }).strict(),
  authority: decisionAnalysisAuthorityV1Schema,
  versions: decisionAnalysisVersionEnvelopeV1Schema,
  upstream: z.array(decisionAnalysisUpstreamReferenceV1Schema),
}).strict().superRefine((input, ctx) => {
  if (input.sed.materialDependencies && input.sed.materialDependencies.reportType !== input.domain) {
    ctx.addIssue({ code: 'custom', path: ['sed', 'materialDependencies', 'reportType'],
      message: 'Material SED dependencies must match the decision domain.' });
  }
  if (input.authority.kind !== 'test-fit') return;
  if (input.domain !== 'test-fit') {
    ctx.addIssue({
      code: 'custom',
      path: ['authority', 'kind'],
      message: 'Test Fit authority may only be attached to the test-fit decision domain.',
    });
  }
  if (
    input.authority.ref.sedId !== input.sed.id
    || input.authority.ref.sedVersion !== input.sed.version
  ) {
    ctx.addIssue({
      code: 'custom',
      path: ['authority', 'ref', 'sedId'],
      message: 'Test Fit authority must reference the same SED id and version as the analysis input.',
    });
  }
});

export type DecisionAnalysisInputEnvelopeV1 =
  z.infer<typeof decisionAnalysisInputEnvelopeV1Schema>;

export const decisionAnalysisSectionV1Schema = z.object({
  id: nonEmptyString,
  sectionId: nonEmptyString,
  title: nonEmptyString,
  group: z.enum(['overview', 'analysis', 'risks-actions', 'evidence']),
  role: z.enum([
    'narrative',
    'kpi',
    'table',
    'chart',
    'map',
    'decision',
    'appendix',
    'legal',
  ]),
  required: z.boolean(),
  blocks: z.array(reportDocumentBlockV1Schema),
}).strict();

export type DecisionAnalysisSectionV1 =
  z.infer<typeof decisionAnalysisSectionV1Schema>;

export const decisionAnalysisSourceIdentityV1Schema = z.object({
  id: nonEmptyString,
  kind: nonEmptyString,
  label: nonEmptyString,
  uri: z.string().url().optional(),
  retrievedAt: isoDateString.optional(),
  contentHash: sha256.optional(),
  authority: z.enum([
    'authoritative',
    'corroborating',
    'derived',
    'advisory',
    'unclassified',
  ]),
}).strict();

export type DecisionAnalysisSourceIdentityV1 =
  z.infer<typeof decisionAnalysisSourceIdentityV1Schema>;

const traceabilityShape = {
  /**
   * Empty is an explicit legacy/unproven provenance state. Adapters must not
   * invent a SED path merely to satisfy the contract.
   */
  sedPaths: z.array(nonEmptyString),
  sourceIds: z.array(nonEmptyString),
  /** Null means the legacy/source payload did not prove a confidence value. */
  confidence: z.number().finite().min(0).max(1).nullable(),
};

export const decisionAnalysisFindingV1Schema = z.object({
  id: nonEmptyString,
  title: nonEmptyString,
  detail: nonEmptyString,
  impact: z.enum(['positive', 'negative', 'neutral']),
  ...traceabilityShape,
}).strict();

export type DecisionAnalysisFindingV1 =
  z.infer<typeof decisionAnalysisFindingV1Schema>;

export const decisionAnalysisRiskV1Schema = z.object({
  id: nonEmptyString,
  label: nonEmptyString,
  severity: z.enum(['critical', 'high', 'medium', 'low']),
  trigger: nonEmptyString,
  mitigation: nonEmptyString,
  ...traceabilityShape,
}).strict();

export type DecisionAnalysisRiskV1 =
  z.infer<typeof decisionAnalysisRiskV1Schema>;

export const decisionAnalysisGapV1Schema = z.object({
  id: nonEmptyString,
  field: nonEmptyString,
  reason: nonEmptyString,
  severity: z.enum(['blocking', 'material', 'minor']),
  wouldChangeDecision: z.boolean(),
  recommendedAction: nonEmptyString,
  sedPaths: z.array(nonEmptyString),
  sourceIds: z.array(nonEmptyString),
}).strict();

export type DecisionAnalysisGapV1 =
  z.infer<typeof decisionAnalysisGapV1Schema>;

export const decisionAnalysisUnknownV1Schema = z.object({
  id: nonEmptyString,
  field: nonEmptyString,
  reason: nonEmptyString,
  materiality: z.enum(['blocking', 'material', 'minor']),
  sedPaths: z.array(nonEmptyString),
  sourceIds: z.array(nonEmptyString),
}).strict();

export type DecisionAnalysisUnknownV1 =
  z.infer<typeof decisionAnalysisUnknownV1Schema>;

export const decisionAnalysisConflictV1Schema = z.object({
  id: nonEmptyString,
  field: nonEmptyString,
  description: nonEmptyString,
  severity: z.enum(['high', 'medium', 'low']),
  status: z.enum(['unresolved', 'resolved', 'accepted']),
  candidates: z.array(z.object({
    value: jsonValueSchema,
    sourceIds: z.array(nonEmptyString).min(1),
  }).strict()).min(2),
  resolution: z.string().nullable(),
  sedPaths: z.array(nonEmptyString),
}).strict().superRefine((conflict, ctx) => {
  if (conflict.status === 'resolved' && !conflict.resolution?.trim()) {
    ctx.addIssue({
      code: 'custom',
      path: ['resolution'],
      message: 'A resolved conflict must record its resolution.',
    });
  }
  if (conflict.status === 'unresolved' && conflict.resolution !== null) {
    ctx.addIssue({
      code: 'custom',
      path: ['resolution'],
      message: 'An unresolved conflict cannot carry a resolution.',
    });
  }
});

export type DecisionAnalysisConflictV1 =
  z.infer<typeof decisionAnalysisConflictV1Schema>;

export const decisionAnalysisConditionV1Schema = z.object({
  id: nonEmptyString,
  label: nonEmptyString,
  requirement: nonEmptyString,
  status: z.enum(['open', 'satisfied', 'waived']),
  sedPaths: z.array(nonEmptyString),
  sourceIds: z.array(nonEmptyString),
}).strict();

export type DecisionAnalysisConditionV1 =
  z.infer<typeof decisionAnalysisConditionV1Schema>;

export const decisionAnalysisRecommendationV1Schema = z.object({
  id: nonEmptyString,
  title: nonEmptyString,
  detail: nonEmptyString,
  /** Null means the source recommendation carried no defensible priority. */
  priority: z.enum(['critical', 'high', 'medium', 'low']).nullable(),
  sedPaths: z.array(nonEmptyString),
  sourceIds: z.array(nonEmptyString),
}).strict();

export type DecisionAnalysisRecommendationV1 =
  z.infer<typeof decisionAnalysisRecommendationV1Schema>;

export const decisionAnalysisBindingConstraintV1Schema = z.object({
  id: nonEmptyString,
  kind: nonEmptyString,
  label: nonEmptyString,
  effect: z.enum(['cap', 'floor', 'block']),
  detail: nonEmptyString,
  sedPaths: z.array(nonEmptyString),
  sourceIds: z.array(nonEmptyString),
}).strict();

export type DecisionAnalysisBindingConstraintV1 =
  z.infer<typeof decisionAnalysisBindingConstraintV1Schema>;

export const decisionAnalysisAdequacyV1Schema = z.object({
  state: z.enum(['adequate', 'inadequate']),
  coverageRatio: z.number().finite().min(0).max(1),
  reason: z.string().nullable(),
  blockingGapIds: z.array(nonEmptyString),
  unresolvedHighConflictIds: z.array(nonEmptyString),
}).strict().superRefine((adequacy, ctx) => {
  if (adequacy.state === 'inadequate' && !adequacy.reason?.trim()) {
    ctx.addIssue({
      code: 'custom',
      path: ['reason'],
      message: 'An inadequate analysis must explain why adequacy was withheld.',
    });
  }
  if (adequacy.state === 'adequate' && adequacy.reason !== null) {
    ctx.addIssue({
      code: 'custom',
      path: ['reason'],
      message: 'An adequate analysis must not carry an inadequacy reason.',
    });
  }
});

export type DecisionAnalysisAdequacyV1 =
  z.infer<typeof decisionAnalysisAdequacyV1Schema>;

const boundedScoreSchema = z.object({
  value: z.number().finite(),
  minimum: z.number().finite(),
  maximum: z.number().finite(),
  higherIsBetter: z.boolean(),
}).strict().superRefine((score, ctx) => {
  if (score.maximum <= score.minimum) {
    ctx.addIssue({
      code: 'custom',
      path: ['maximum'],
      message: 'Score maximum must be greater than score minimum.',
    });
  }
  if (score.value < score.minimum || score.value > score.maximum) {
    ctx.addIssue({
      code: 'custom',
      path: ['value'],
      message: 'Score value must fall within its declared scale.',
    });
  }
});

export const decisionAnalysisScoreV1Schema = z.object({
  overall: boundedScoreSchema,
  /** Null means the source scorer did not emit a calibrated confidence. */
  confidence: z.number().finite().min(0).max(1).nullable(),
  components: z.array(z.object({
    id: nonEmptyString,
    label: nonEmptyString,
    score: boundedScoreSchema,
    weight: z.number().finite().min(0),
    sedPaths: z.array(nonEmptyString),
    sourceIds: z.array(nonEmptyString),
  }).strict()),
}).strict();

export type DecisionAnalysisScoreV1 =
  z.infer<typeof decisionAnalysisScoreV1Schema>;

export const decisionAnalysisSystemDecisionV1Schema = z.object({
  band: z.enum(['go', 'conditional', 'no-go', 'withheld']),
  basis: z.literal('deterministic'),
  rationale: nonEmptyString,
  conditionIds: z.array(nonEmptyString),
  bindingConstraintIds: z.array(nonEmptyString),
}).strict();

export type DecisionAnalysisSystemDecisionV1 =
  z.infer<typeof decisionAnalysisSystemDecisionV1Schema>;

/**
 * Narrative is explanatory and non-authoritative, but written narrative is a
 * prerequisite for final GO/CONDITIONAL readiness. Deterministic preliminary
 * findings remain viewable while this lifecycle is pending or failed.
 */
export const decisionAnalysisNarrativeStateV1Schema = z.discriminatedUnion('state', [
  z.object({
    state: z.literal('not-requested'),
  }).strict(),
  z.object({
    state: z.literal('pending'),
    requestedAt: isoDateString,
    model: nonEmptyString,
    promptVersion: nonEmptyString,
  }).strict(),
  z.object({
    state: z.literal('ready'),
    generatedAt: isoDateString,
    model: nonEmptyString.nullable(),
    promptVersion: nonEmptyString.nullable(),
    generationMethod: z.enum(['model', 'deterministic-source-screening']).optional(),
    constructionVersion: nonEmptyString.optional(),
  }).strict(),
  z.object({
    state: z.literal('degraded'),
    generatedAt: isoDateString,
    model: nonEmptyString,
    promptVersion: nonEmptyString,
    reason: nonEmptyString,
  }).strict(),
  z.object({
    state: z.literal('failed'),
    attemptedAt: isoDateString,
    model: nonEmptyString.nullable(),
    promptVersion: nonEmptyString.nullable(),
    reason: nonEmptyString,
  }).strict(),
]).superRefine((narrative, ctx) => {
  if (narrative.state !== 'ready') return;
  if (narrative.generationMethod === 'deterministic-source-screening') {
    if (narrative.model !== null || narrative.promptVersion !== null || !narrative.constructionVersion) {
      ctx.addIssue({ code: 'custom', path: ['generationMethod'], message: 'Deterministic written screening requires its construction version and must not claim model or prompt provenance.' });
    }
  } else if (!narrative.model || !narrative.promptVersion) {
    ctx.addIssue({ code: 'custom', path: ['model'], message: 'Model-generated narrative requires both model and prompt versions.' });
  }
});

export type DecisionAnalysisNarrativeStateV1 =
  z.infer<typeof decisionAnalysisNarrativeStateV1Schema>;

export const decisionAnalysisQualityFindingV1Schema = z.object({
  code: nonEmptyString,
  severity: z.enum(['info', 'warning', 'block']),
  message: nonEmptyString,
  sourceIds: z.array(nonEmptyString),
}).strict();

export const decisionAnalysisQualityDecisionV1Schema = z.object({
  state: z.enum(['pass', 'held', 'degraded', 'failed']),
  hardGatesPassed: z.boolean(),
  findings: z.array(decisionAnalysisQualityFindingV1Schema),
  evaluatedAt: isoDateString,
  policyVersion: nonEmptyString,
  validationMethod: z.literal('deterministic-source-screening').optional(),
  reviewerVerdict: z.enum(['pass', 'needs-revision', 'not-run']).optional(),
}).strict().superRefine((quality, ctx) => {
  const hasBlockingFinding = quality.findings.some(finding => finding.severity === 'block');
  if (quality.state === 'pass' && (!quality.hardGatesPassed || hasBlockingFinding)) {
    ctx.addIssue({
      code: 'custom',
      path: ['state'],
      message: 'Passing quality requires all hard gates to pass and no blocking findings.',
    });
  }
  if (quality.state !== 'pass' && quality.findings.length === 0) {
    ctx.addIssue({
      code: 'custom',
      path: ['findings'],
      message: 'A non-passing quality decision must explain at least one finding.',
    });
  }
});

export type DecisionAnalysisQualityDecisionV1 =
  z.infer<typeof decisionAnalysisQualityDecisionV1Schema>;

export const decisionAnalysisSemanticContentV1Schema = z.object({
  title: nonEmptyString,
  summary: z.string(),
  sections: z.array(decisionAnalysisSectionV1Schema),
  omissions: z.array(documentOmissionV1Schema),
  sources: z.array(decisionAnalysisSourceIdentityV1Schema),
  citations: z.array(documentCitationV1Schema),
  claims: z.array(documentClaimV1Schema),
  evidence: z.record(z.string(), evidenceValueJsonSchema),
  findings: z.array(decisionAnalysisFindingV1Schema),
  risks: z.array(decisionAnalysisRiskV1Schema),
  gaps: z.array(decisionAnalysisGapV1Schema),
  unknowns: z.array(decisionAnalysisUnknownV1Schema),
  conflicts: z.array(decisionAnalysisConflictV1Schema),
  conditions: z.array(decisionAnalysisConditionV1Schema),
  recommendations: z.array(decisionAnalysisRecommendationV1Schema),
  bindingConstraints: z.array(decisionAnalysisBindingConstraintV1Schema),
  adequacy: decisionAnalysisAdequacyV1Schema,
  score: decisionAnalysisScoreV1Schema.nullable(),
  systemDecision: decisionAnalysisSystemDecisionV1Schema,
  narrative: decisionAnalysisNarrativeStateV1Schema,
  quality: decisionAnalysisQualityDecisionV1Schema,
}).strict().superRefine((content, ctx) => {
  const requireUniqueIds = (
    rows: readonly { id: string }[],
    path: string,
    label: string,
  ): void => {
    const seen = new Set<string>();
    for (const [index, row] of rows.entries()) {
      if (seen.has(row.id)) {
        ctx.addIssue({
          code: 'custom',
          path: [path, index, 'id'],
          message: `${label} IDs must be unique.`,
        });
      }
      seen.add(row.id);
    }
  };
  requireUniqueIds(content.sections, 'sections', 'Decision-analysis section');
  requireUniqueIds(content.sources, 'sources', 'Source identity');
  requireUniqueIds(content.citations, 'citations', 'Citation');
  requireUniqueIds(content.claims, 'claims', 'Claim');
  requireUniqueIds(content.findings, 'findings', 'Finding');
  requireUniqueIds(content.risks, 'risks', 'Risk');
  requireUniqueIds(content.gaps, 'gaps', 'Gap');
  requireUniqueIds(content.unknowns, 'unknowns', 'Unknown');
  requireUniqueIds(content.conflicts, 'conflicts', 'Conflict');
  requireUniqueIds(content.conditions, 'conditions', 'Condition');
  requireUniqueIds(content.recommendations, 'recommendations', 'Recommendation');
  requireUniqueIds(
    content.bindingConstraints,
    'bindingConstraints',
    'Binding constraint',
  );

  const sectionIds = new Set<string>();
  const blockIds = new Set<string>();
  for (const [sectionIndex, section] of content.sections.entries()) {
    if (sectionIds.has(section.sectionId)) {
      ctx.addIssue({
        code: 'custom',
        path: ['sections', sectionIndex, 'sectionId'],
        message: 'Decision-analysis section IDs must be unique.',
      });
    }
    sectionIds.add(section.sectionId);
    // An empty required section is acceptable ONLY when an explicit omission
    // record says why it is empty — the same contract the source document model
    // enforces (`reportContractsV2`: "An empty section requires an explicit
    // omission record").
    //
    // These two schemas disagreed, and this one was both stricter and less
    // informed: `buildReportDocumentModelV1` deliberately emits `blocks: []`
    // plus an omission rather than inventing favorable content, and this rule
    // then rejected its own upstream's designed output. Because the analysis is
    // built during finalization, the throw discarded a FULLY GENERATED report —
    // minutes of model work, nothing persisted, and a message
    // ("sections.3.blocks") that named no section a user could act on. Under the
    // truth-only policy an unevidenced section is a completeness gap, not a
    // demonstrable untruth, so it must hold and surface, never abort.
    //
    // The guard is NOT removed: an empty section with no omission record still
    // fails, which is the shape a resolution/wiring bug actually takes.
    const omissionForSection = content.omissions.find(
      (omission) => omission.sectionId === section.sectionId,
    );
    if (section.blocks.length === 0 && !omissionForSection) {
      ctx.addIssue({
        code: 'custom',
        path: ['sections', sectionIndex, 'blocks'],
        message: 'An empty decision-analysis section requires an explicit omission record.',
      });
    }
    for (const [blockIndex, block] of section.blocks.entries()) {
      if (blockIds.has(block.id)) {
        ctx.addIssue({
          code: 'custom',
          path: ['sections', sectionIndex, 'blocks', blockIndex, 'id'],
          message: 'Decision-analysis block IDs must be globally unique.',
        });
      }
      blockIds.add(block.id);
    }
  }

  const sourceIds = new Set(content.sources.map(source => source.id));
  const citationIds = new Set(content.citations.map(citation => citation.id));
  const requireKnownSourceIds = (
    ids: readonly string[],
    path: Array<string | number>,
  ): void => {
    for (const [index, id] of ids.entries()) {
      if (!sourceIds.has(id)) {
        ctx.addIssue({
          code: 'custom',
          path: [...path, index],
          message: 'Traceability references an unknown source identity.',
        });
      }
    }
  };
  const requireKnownCitationIds = (
    ids: readonly string[],
    path: Array<string | number>,
  ): void => {
    for (const [index, id] of ids.entries()) {
      if (!citationIds.has(id)) {
        ctx.addIssue({
          code: 'custom',
          path: [...path, index],
          message: 'Traceability references an unknown citation.',
        });
      }
    }
  };

  for (const [citationIndex, citation] of content.citations.entries()) {
    if (!sourceIds.has(citation.id)) {
      ctx.addIssue({
        code: 'custom',
        path: ['citations', citationIndex, 'id'],
        message: 'Every citation must have a matching source identity.',
      });
    }
  }
  for (const [sectionIndex, section] of content.sections.entries()) {
    for (const [blockIndex, block] of section.blocks.entries()) {
      requireKnownSourceIds(
        block.sourceIds,
        ['sections', sectionIndex, 'blocks', blockIndex, 'sourceIds'],
      );
      requireKnownCitationIds(
        block.citationIds,
        ['sections', sectionIndex, 'blocks', blockIndex, 'citationIds'],
      );
    }
  }
  for (const [claimIndex, claim] of content.claims.entries()) {
    requireKnownSourceIds(claim.sourceIds, ['claims', claimIndex, 'sourceIds']);
    requireKnownCitationIds(
      claim.citationIds,
      ['claims', claimIndex, 'citationIds'],
    );
  }
  for (const [field, value] of Object.entries(content.evidence)) {
    requireKnownSourceIds(value.sourceIds, ['evidence', field, 'sourceIds']);
  }
  for (
    const [collection, rows]
    of [
      ['findings', content.findings],
      ['risks', content.risks],
      ['gaps', content.gaps],
      ['unknowns', content.unknowns],
      ['conditions', content.conditions],
      ['recommendations', content.recommendations],
      ['bindingConstraints', content.bindingConstraints],
    ] as const
  ) {
    for (const [index, row] of rows.entries()) {
      requireKnownSourceIds(row.sourceIds, [collection, index, 'sourceIds']);
    }
  }
  for (const [conflictIndex, conflict] of content.conflicts.entries()) {
    for (const [candidateIndex, candidate] of conflict.candidates.entries()) {
      requireKnownSourceIds(
        candidate.sourceIds,
        ['conflicts', conflictIndex, 'candidates', candidateIndex, 'sourceIds'],
      );
    }
  }
  for (const [componentIndex, component] of (
    content.score?.components ?? []
  ).entries()) {
    requireKnownSourceIds(
      component.sourceIds,
      ['score', 'components', componentIndex, 'sourceIds'],
    );
  }
  for (const [findingIndex, finding] of content.quality.findings.entries()) {
    requireKnownSourceIds(
      finding.sourceIds,
      ['quality', 'findings', findingIndex, 'sourceIds'],
    );
  }

  const conditionIds = new Set(content.conditions.map(condition => condition.id));
  // A conditional verdict that names no conditions is incoherent, and it stays
  // rejected for any analysis claiming to be ADEQUATE. But `fromReport` already
  // detects this exact state ("The conditional report verdict does not provide
  // explicit decision conditions.") and marks the analysis inadequate for it —
  // and an inadequate analysis is by definition not decision-ready. Aborting on
  // top of that discarded the whole generated report instead of surfacing the
  // hold, which is how two public-sentiment reports died on 2026-07-29.
  if (
    content.systemDecision.band === 'conditional'
    && content.systemDecision.conditionIds.length === 0
    && content.adequacy.state !== 'inadequate'
  ) {
    ctx.addIssue({
      code: 'custom',
      path: ['systemDecision', 'conditionIds'],
      message: 'An adequate conditional decision must reference at least one explicit condition.',
    });
  }
  for (const [index, conditionId] of content.systemDecision.conditionIds.entries()) {
    if (!conditionIds.has(conditionId)) {
      ctx.addIssue({
        code: 'custom',
        path: ['systemDecision', 'conditionIds', index],
        message: 'System decision references an unknown condition.',
      });
    }
  }
  const constraintIds = new Set(
    content.bindingConstraints.map(constraint => constraint.id),
  );
  for (
    const [index, constraintId]
    of content.systemDecision.bindingConstraintIds.entries()
  ) {
    if (!constraintIds.has(constraintId)) {
      ctx.addIssue({
        code: 'custom',
        path: ['systemDecision', 'bindingConstraintIds', index],
        message: 'System decision references an unknown binding constraint.',
      });
    }
  }
  const gapIds = new Set(content.gaps.map(gap => gap.id));
  for (const [index, gapId] of content.adequacy.blockingGapIds.entries()) {
    if (!gapIds.has(gapId)) {
      ctx.addIssue({
        code: 'custom',
        path: ['adequacy', 'blockingGapIds', index],
        message: 'Adequacy references an unknown blocking gap.',
      });
    }
  }
  const highConflictIds = new Set(
    content.conflicts
      .filter(conflict => conflict.severity === 'high' && conflict.status === 'unresolved')
      .map(conflict => conflict.id),
  );
  for (
    const [index, conflictId]
    of content.adequacy.unresolvedHighConflictIds.entries()
  ) {
    if (!highConflictIds.has(conflictId)) {
      ctx.addIssue({
        code: 'custom',
        path: ['adequacy', 'unresolvedHighConflictIds', index],
        message: 'Adequacy references a conflict that is not unresolved and high severity.',
      });
    }
  }
});

export type DecisionAnalysisSemanticContentV1 =
  z.infer<typeof decisionAnalysisSemanticContentV1Schema>;

/**
 * Presentation-neutral document projection consumed by the authenticated web
 * dossier and by compatibility exporters. It intentionally carries no page
 * size, pagination, theme, layout snapshot, or rendered-artifact pointer.
 */
export const decisionAnalysisDocumentV1Schema = z.object({
  schemaVersion: z.literal(DECISION_ANALYSIS_DOCUMENT_SCHEMA_VERSION),
  analysisId: nonEmptyString,
  analysisContentHash: sha256,
  siteId: nonEmptyString,
  domain: decisionAnalysisDomainSchema,
  inputHash: sha256,
  createdAt: isoDateString,
  title: nonEmptyString,
  summary: z.string(),
  sections: z.array(decisionAnalysisSectionV1Schema),
  omissions: z.array(documentOmissionV1Schema),
  sources: z.array(decisionAnalysisSourceIdentityV1Schema),
  citations: z.array(documentCitationV1Schema),
  claims: z.array(documentClaimV1Schema),
  evidence: z.record(z.string(), evidenceValueJsonSchema),
  quality: decisionAnalysisQualityDecisionV1Schema,
}).strict();

export type DecisionAnalysisDocumentV1 =
  z.infer<typeof decisionAnalysisDocumentV1Schema>;

export const decisionAnalysisSnapshotV1Schema = z.object({
  schemaVersion: z.literal(DECISION_ANALYSIS_SCHEMA_VERSION),
  hashVersion: decisionAnalysisHashVersionSchema,
  analysisId: nonEmptyString,
  inputHash: sha256,
  contentHash: sha256,
  createdAt: isoDateString,
  siteId: nonEmptyString,
  domain: decisionAnalysisDomainSchema,
  input: decisionAnalysisInputEnvelopeV1Schema,
  semantic: decisionAnalysisSemanticContentV1Schema,
}).strict().superRefine((snapshot, ctx) => {
  if (snapshot.hashVersion !== snapshot.input.hashVersion) {
    ctx.addIssue({
      code: 'custom',
      path: ['hashVersion'],
      message: 'Snapshot hashVersion must match the input envelope hashVersion.',
    });
  }
  if (snapshot.siteId !== snapshot.input.siteId) {
    ctx.addIssue({
      code: 'custom',
      path: ['siteId'],
      message: 'Snapshot siteId must match the input envelope.',
    });
  }
  if (snapshot.domain !== snapshot.input.domain) {
    ctx.addIssue({
      code: 'custom',
      path: ['domain'],
      message: 'Snapshot domain must match the input envelope.',
    });
  }
});

export type DecisionAnalysisSnapshotV1 =
  z.infer<typeof decisionAnalysisSnapshotV1Schema>;

/**
 * Public contract name for an immutable decision-analysis revision.
 *
 * `DecisionAnalysisSnapshotV1` remains available for compatibility with the
 * report-generation code that introduced this storage shape.
 */
export const decisionAnalysisRevisionV1Schema =
  decisionAnalysisSnapshotV1Schema;

export type DecisionAnalysisRevisionV1 = DecisionAnalysisSnapshotV1;

export const analysisReadinessReasonCodeSchema = z.enum([
  'analysis-missing',
  'snapshot-schema-invalid',
  'snapshot-identity-invalid',
  'current-input-invalid',
  'input-stale',
  'quality-held',
  'quality-degraded',
  'quality-failed',
  'hard-gate-failed',
  'narrative-pending',
  'narrative-degraded',
  'narrative-failed',
  'narrative-not-requested',
  'data-inadequate',
]);

export type AnalysisReadinessReasonCode =
  z.infer<typeof analysisReadinessReasonCodeSchema>;

export const analysisReadinessReasonV1Schema = z.object({
  code: analysisReadinessReasonCodeSchema,
  message: nonEmptyString,
}).strict();

export type AnalysisReadinessReasonV1 =
  z.infer<typeof analysisReadinessReasonV1Schema>;

export const analysisReadinessStateSchema = z.enum([
  'missing',
  'queued',
  'refreshing',
  'ready',
  'stale',
  'held',
  'degraded',
  'failed',
]);

export type AnalysisReadinessState = z.infer<typeof analysisReadinessStateSchema>;

/**
 * Presentation-independent readiness decision. No export/artifact/PDF state is
 * accepted by this schema.
 */
export const analysisReadinessDecisionV1Schema = z.object({
  schemaVersion: z.literal(ANALYSIS_READINESS_DECISION_SCHEMA_VERSION),
  state: analysisReadinessStateSchema,
  ready: z.boolean(),
  canView: z.boolean(),
  canUseForDecision: z.boolean(),
  current: z.boolean(),
  identityValid: z.boolean(),
  qualityReady: z.boolean(),
  narrativeReady: z.boolean(),
  reason: analysisReadinessReasonV1Schema.nullable(),
  reasons: z.array(analysisReadinessReasonV1Schema),
  evaluatedAt: isoDateString,
}).strict().superRefine((decision, ctx) => {
  const fullyReady = (
    decision.current
    && decision.identityValid
    && decision.qualityReady
    && decision.narrativeReady
    && decision.reasons.length === 0
  );
  if (decision.canUseForDecision !== fullyReady) {
    ctx.addIssue({
      code: 'custom',
      path: ['canUseForDecision'],
      message: 'Decision eligibility must equal current, valid, quality-passed written analysis.',
    });
  }
  if (decision.ready !== decision.canUseForDecision) {
    ctx.addIssue({
      code: 'custom',
      path: ['ready'],
      message: 'Ready must match decision eligibility.',
    });
  }
  if ((decision.state === 'ready') !== decision.canUseForDecision) {
    ctx.addIssue({
      code: 'custom',
      path: ['state'],
      message: 'Only a decision-eligible analysis may have ready state.',
    });
  }
  if (
    !decision.canView
    && decision.state !== 'missing'
    && decision.state !== 'failed'
  ) {
    ctx.addIssue({
      code: 'custom',
      path: ['canView'],
      message: 'A non-viewable analysis must be missing or failed.',
    });
  }
  const firstReason = decision.reasons[0] ?? null;
  if (
    (decision.reason === null) !== (firstReason === null)
    || (
      decision.reason !== null
      && firstReason !== null
      && (
        decision.reason.code !== firstReason.code
        || decision.reason.message !== firstReason.message
      )
    )
  ) {
    ctx.addIssue({
      code: 'custom',
      path: ['reason'],
      message: 'Reason must be the first readiness reason, or null when there are none.',
    });
  }
});

export type AnalysisReadinessDecisionV1 =
  z.infer<typeof analysisReadinessDecisionV1Schema>;

/** Version-neutral name used by workflow and UI consumers. */
export type AnalysisReadinessDecision = AnalysisReadinessDecisionV1;

export const analysisRevisionReferenceV1Schema = z.object({
  schemaVersion: z.literal(ANALYSIS_REVISION_REFERENCE_SCHEMA_VERSION),
  analysisId: nonEmptyString,
  siteId: nonEmptyString,
  domain: decisionAnalysisDomainSchema,
  inputHash: sha256,
  contentHash: sha256,
  snapshotPath: nonEmptyString,
  createdAt: isoDateString,
  readiness: analysisReadinessDecisionV1Schema,
}).strict();

export type AnalysisRevisionReferenceV1 =
  z.infer<typeof analysisRevisionReferenceV1Schema>;
